import { Avatar, Body, Icon } from '@assembly-js/design-system';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TextField } from 'src/components/TextField';
import { Client } from 'src/constants';
import { RenderGroup } from 'src/legacy/components/Select/GroupListBox';
import { getInitials } from 'src/utils/UserUtils';

const filter = createFilterOptions<SelectClientOption>();
export type SelectClientOption = {
  label: string;
  value: string;
  client: Client | null;
};

const ClientOptionItem = ({
  client,
  label: inputValue,
}: {
  client: Client | null;
  label: string;
}) => {
  if (!client) {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="Plus" className="w-3.5 h-3.5" />
        <Body size="sm">Create {inputValue}</Body>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Avatar
        size="sm"
        variant="circle"
        src={client.fields.avatarImageUrl}
        fallbackColor={client.fields.fallbackColor}
        text={getInitials(
          `${client.fields.givenName} ${client.fields.familyName}`,
          {
            twoLetters: true,
          },
        )}
        alt={`${client.fields.givenName} ${client.fields.familyName}`}
      />
      <Body size="sm">
        {client.fields.givenName} {client.fields.familyName}
      </Body>
    </div>
  );
};
export const SelectClients = ({
  clients,
  onChange,
  onBlur,
}: {
  clients: Client[];
  onChange: (newSelectedClient: SelectClientOption | null) => void;
  onBlur: () => void;
}) => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    return clients.map((client) => ({
      label: `${client.fields.givenName} ${client.fields.familyName}`,
      value: client.id,
      client: client,
    }));
  }, [clients]);

  return (
    <Autocomplete<SelectClientOption, false, false, false>
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          fullWidth
          autoFocus
          placeholder={t(($) => $.messages.autocomplete_find_or_create_client)}
        />
      )}
      onChange={(_, value) => onChange(value)}
      onBlur={onBlur}
      renderGroup={RenderGroup}
      groupBy={(option) => (option.client ? 'Recent Clients' : '')}
      renderOption={(option) => (
        <ClientOptionItem client={option.client} label={option.label} />
      )}
      filterOptions={(opts, params) => {
        const trimmedInputValue = params.inputValue.trim();
        let filtered = filter(opts, params);
        if (trimmedInputValue) {
          const exitingOption = filtered.find(
            (o) => o.label.toLowerCase() === params.inputValue.toLowerCase(),
          );

          filtered = [
            ...(!exitingOption
              ? [
                  {
                    label: params.inputValue,
                    value: params.inputValue,
                    client: null,
                  },
                ]
              : []),
            ...filtered,
          ];
        }
        return filtered.slice(0, 6);
      }}
      getOptionLabel={(option) => option.label}
      openOnFocus
    />
  );
};
